import { Invoice } from '@/types/invoice';

/**
 * Calculates revenue that actually entered via App Sporto (Payment Gateway or QRIS).
 * If paid or settled manually outside the app (Cash/Transfer), it is excluded (0).
 * If booked via In-App with DP 50% and pelunasan was done manually, ONLY the 50% DP is counted.
 */
export function getAppSportoRevenue(inv: Invoice): number {
  const status = String(inv.payment_status || '').toUpperCase();
  const txType = String(inv.transaction_type || '').toLowerCase();
  const method = String(inv.payment_method || inv.payment_type || '').toLowerCase();
  const num = String(inv.invoice_number || '').toUpperCase();
  const isPelunasan = status === 'PELUNASAN' || num.endsWith('-PL');

  // If this invoice is Pelunasan:
  if (isPelunasan) {
    // Only count if pelunasan was paid via QRIS or online app gateway
    const isManualMethod =
      method.includes('cash') ||
      method.includes('tunai') ||
      method.includes('transfer') ||
      method.includes('tf') ||
      method.includes('bank');

    if (isManualMethod) {
      return 0; // Manual pelunasan from outside: do NOT count into App revenue!
    }
    return inv.amount || inv.paid_amount || inv.total_amount || 0;
  }

  // If manual walk-in or POS:
  if (txType === 'manual' || num.startsWith('POS-')) {
    // Check if paid via QRIS (which goes through Sporto gateway)
    if (!method.includes('qris')) {
      return 0; // Manual input non-QRIS: do NOT count!
    }
  }

  // If method is cash/transfer walk-in:
  if (
    method.includes('cash') ||
    method.includes('tunai') ||
    method.includes('transfer') ||
    method.includes('tf') ||
    method.includes('bank')
  ) {
    return 0;
  }

  // Check payment status:
  // Only Paid or DP via App Sporto counts!
  if (status === 'PAID' || status === 'LUNAS') {
    const isDpOrder =
      inv.payment_type?.toLowerCase() === 'dp_balance' ||
      inv.payment_type?.toLowerCase() === 'dp' ||
      (typeof inv.dp_amount === 'number' && inv.dp_amount > 0 && inv.dp_amount < inv.total_amount);

    if (isDpOrder) {
      // In-app DP portion only:
      return inv.dp_amount || inv.paid_amount || Math.round((inv.total_amount || inv.amount || 0) * 0.5);
    }
    return inv.amount || inv.total_amount || 0;
  }

  if (
    status === 'DP' ||
    status === 'DP_PAID' ||
    status === 'DP 50%' ||
    status.includes('DP') ||
    status === 'PARTIAL'
  ) {
    // DP 50% paid via App:
    return inv.dp_amount || inv.paid_amount || Math.round((inv.total_amount || inv.amount || 0) * 0.5);
  }

  // UNPAID, PENDING, CANCELLED -> 0
  return 0;
}

/**
 * Checks whether an invoice represents a valid confirmed booking that entered via App Sporto (Lunas or DP).
 */
export function isAppSportoBooking(inv: Invoice): boolean {
  const status = String(inv.payment_status || '').toUpperCase();
  const txType = String(inv.transaction_type || '').toLowerCase();
  const method = String(inv.payment_method || inv.payment_type || '').toLowerCase();
  const num = String(inv.invoice_number || '').toUpperCase();

  // Pelunasan is a balance settlement, not a separate booking
  if (status === 'PELUNASAN' || num.endsWith('-PL')) return false;

  // POS products are not court bookings
  if (num.startsWith('POS-') || inv.order_type === 'product_purchase') return false;

  // Manual walk-in non-QRIS is not App Sporto booking
  if (txType === 'manual' && !method.includes('qris')) return false;
  if (
    method.includes('cash') ||
    method.includes('tunai') ||
    method.includes('transfer') ||
    method.includes('tf') ||
    method.includes('bank')
  ) {
    return false;
  }

  // Must be Lunas or DP
  const isPaidOrDp =
    status === 'PAID' ||
    status === 'LUNAS' ||
    status === 'DP' ||
    status === 'DP_PAID' ||
    status === 'DP 50%' ||
    status.includes('DP') ||
    inv.payment_type === 'dp_balance';

  return isPaidOrDp;
}

/**
 * Checks if a transaction is subject to platform fee.
 * Platform fee only applies to In-App Sporto orders and QRIS payments.
 * Manual input (Cash/Transfer walk-in) is 0 platform fee.
 */
export function isAppOrQrisTransaction(inv?: Invoice | null, detail?: any): boolean {
  if (!inv && !detail) return false;
  const num = String(inv?.invoice_number || detail?.invoice_number || '').toUpperCase();
  if (num.startsWith('POS-')) return false;

  const method = String(
    inv?.payment_method || detail?.payment_method || inv?.payment_type || detail?.payment_type || ''
  ).toLowerCase();

  // QRIS is processed via payment gateway (App Sporto) -> Platform Fee applies
  if (method.includes('qris')) {
    return true;
  }

  // Pelunasan: if paid with Cash or Transfer outside app, NO fee
  const isPelunasan =
    String(inv?.payment_status || detail?.status || '').toUpperCase() === 'PELUNASAN' ||
    num.endsWith('-PL');
  if (isPelunasan) {
    if (
      method.includes('cash') ||
      method.includes('tunai') ||
      method.includes('transfer') ||
      method.includes('tf') ||
      method.includes('bank')
    ) {
      return false;
    }
  }

  // Manual walk-in non-QRIS -> NO platform fee
  const txType = String(inv?.transaction_type || '').toLowerCase();
  const orderMethod = String(detail?.order_method || inv?.order_type || '').toLowerCase();
  if (txType === 'manual' || orderMethod === 'onsite' || orderMethod === 'walk_in') {
    return false;
  }

  if (
    method.includes('cash') ||
    method.includes('tunai') ||
    method.includes('transfer') ||
    method.includes('tf') ||
    method.includes('bank')
  ) {
    return false;
  }

  // In-App Sporto booking/order -> Platform fee applies
  return true;
}
